import { ChangeEvent, useMemo } from "react";
import { updateGlobalUserPreference } from "modules/globalUserPreference";
import CameraSelector from "modules/streaming/devices/CameraSelector";
import MicrophoneSelector from "modules/streaming/devices/MicrophoneSelector";
import SpeakerSelector from "modules/streaming/devices/SpeakerSelector";
import { SerializedDevice, supportsSetSinkId } from "modules/deviceInfo";
import { useDevices, useUserData } from "modules/audioVideo/hooks";
import { Checkbox } from "@remo-co/ui-core/src/components/Checkbox";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { useI18n } from "i18n";
import { Mic } from "@remo-co/ui-core/src/icons/Mic";
import { Videocam } from "@remo-co/ui-core/src/icons/Videocam";
import { VolumeUp } from "@remo-co/ui-core/src/icons/VolumeUp";
import { useSelector } from "react-redux";
import { selectAspectRatioPreference } from "modules/userPreferences/redux/selectors";
import { useStyles } from "./styles";
import { AudioTest } from "./AudioTest";
import { LoadingDevices } from "./LoadingDevices";

export const DeviceSelection = () => {
  const {
    activeDevices,
    setCamera,
    setMicrophone,
    setSpeaker,
    microphones,
    cameras,
    speakers,
  } = useDevices();
  const { setUserData } = useUserData();

  const { t } = useI18n(["common", "micCamCheck", "conversation"]);

  const originalAspectRatio = useSelector(selectAspectRatioPreference);

  const styles = useStyles();

  const activeAudioInput = activeDevices.audioInput;
  const activeVideoInput = activeDevices.videoInput;
  const activeAudioOutput = activeDevices.audioOutput;

  const devicesLoading = useMemo(
    () => cameras.length === 0 && microphones.length === 0,
    [cameras, microphones],
  );
  const handleAspectRatioToggle = (
    _event: ChangeEvent<HTMLInputElement>,
    originalAspectRatio: boolean,
  ) => {
    setUserData({ originalAspectRatio });
    updateGlobalUserPreference({
      cameraSettings: { originalAspectRatio },
    });
  };

  const handleVideoInputChange = (newDevice: SerializedDevice) => {
    setCamera(newDevice);
  };

  const handleAudioInputChange = (newDevice: SerializedDevice) => {
    setMicrophone(newDevice);
  };

  const handleAudioOutputChange = (newDevice: SerializedDevice) => {
    setSpeaker(newDevice);
  };

  return (
    <Container
      flex
      column
      className={styles.deviceSelection}
      data-testid="device-selection"
    >
      {devicesLoading ? (
        <LoadingDevices />
      ) : (
        <>
          <div className={styles.selectorRow}>
            <CameraSelector
              icon={<Videocam />}
              outerLabel={t("camera")}
              selected={activeVideoInput}
              onChange={handleVideoInputChange}
              devices={cameras}
              fullWidth
            />
          </div>
          <div className={styles.selectorRow}>
            <MicrophoneSelector
              icon={<Mic />}
              outerLabel={t("microphone")}
              selected={activeAudioInput}
              onChange={handleAudioInputChange}
              devices={microphones}
              fullWidth
            />
          </div>
          <div className={styles.selectorRow}>
            {supportsSetSinkId() && (
              <SpeakerSelector
                icon={<VolumeUp />}
                outerLabel={t("speaker")}
                selected={activeAudioOutput}
                onChange={handleAudioOutputChange}
                devices={speakers}
                fullWidth
              />
            )}
          </div>
          <div className={styles.selectorRow}>
            <AudioTest />
          </div>
          <div className={styles.selectorRow}>
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="aspect-ratio-toggle"
                  color="primary"
                  checked={originalAspectRatio}
                  onChange={handleAspectRatioToggle}
                />
              }
              label={t("conversation:aspect.ratio.toggle")}
            />
          </div>
        </>
      )}
    </Container>
  );
};
